import React from "react";
import "../styles/faq-section.css";

/**
 * FaqSection — reusable accordion using native <details>/<summary> so the
 * answer text is in the DOM before interaction (required for FAQPage rich
 * results and readable by no-JS / AI crawlers). Flow layout, tokens only.
 *
 * Props:
 *   eyebrow  - small label (default "Frequently asked questions")
 *   title    - section H2
 *   faqs     - [{ question, answer }]  (answer = string or JSX)
 *   emitJsonLd - if true, also renders FAQPage JSON-LD
 */
export default function FaqSection({
  eyebrow = "Frequently asked questions",
  title = "FAQ",
  faqs = [],
  emitJsonLd = false,
}) {
  const jsonLd = emitJsonLd
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: typeof f.answer === "string" ? f.answer : f.plain || "",
          },
        })),
      }
    : null;

  return (
    <section className="faq-section">
      <header className="faq-section__header">
        <p className="why-cloudare__eyebrow">{eyebrow}</p>
        <h2 className="faq-section__title">{title}</h2>
      </header>

      <div className="faq-section__list">
        {faqs.map((faq, i) => (
          <details className="faq-item" key={i}>
            <summary className="faq-item__question">
              <span>{faq.question}</span>
              <span className="faq-item__chevron" aria-hidden="true">
                +
              </span>
            </summary>
            <div className="faq-item__answer">
              {typeof faq.answer === "string" ? <p>{faq.answer}</p> : faq.answer}
            </div>
          </details>
        ))}
      </div>

      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </section>
  );
}
